import { useCallback, useRef, useState } from 'react'

export const PANEL_MIN_PX = 400
export const PANEL_MAX_VW = 0.72 // max 72% of viewport (lets the wide default render)
export const PANEL_DEFAULT_FRACTION = 0.63 // panel ~63%, map ~37% (Split target: compact map, wide dashboard)

export interface SplitPane {
  open: boolean
  full: boolean
  /** Panel width in px; null = default fraction (flex-basis percentage). */
  widthPx: number | null
  setOpen: (open: boolean) => void
  toggleFull: () => void
  /** pointerdown handler for the drag handle. */
  onHandlePointerDown: (e: React.PointerEvent<HTMLDivElement>) => void
}

export function useSplitPane(): SplitPane {
  const [open, setOpenState] = useState(true)
  // Default to Full view (map hidden, full-width dashboard) on load; the Split toggle switches to the map.
  const [full, setFull] = useState(true)
  const [widthPx, setWidthPx] = useState<number | null>(null)
  // Remember the drag width across close/reopen (design: reopen restores the previous split).
  const lastWidth = useRef<number | null>(null)

  const setOpen = useCallback((next: boolean) => {
    setOpenState(next)
    if (!next) {
      setFull(false) // closing while expanded must not hide both panes
    } else {
      setWidthPx(lastWidth.current)
    }
  }, [])

  const toggleFull = useCallback(() => setFull((f) => !f), [])

  const onHandlePointerDown = useCallback((e: React.PointerEvent<HTMLDivElement>) => {
    e.preventDefault()
    const handle = e.currentTarget
    handle.setPointerCapture(e.pointerId)
    const onMove = (ev: PointerEvent) => {
      const w = Math.min(
        window.innerWidth * PANEL_MAX_VW,
        Math.max(PANEL_MIN_PX, window.innerWidth - ev.clientX),
      )
      lastWidth.current = w
      setWidthPx(w)
    }
    const cleanup = () => {
      handle.removeEventListener('pointermove', onMove)
      handle.removeEventListener('pointerup', cleanup)
      handle.removeEventListener('pointercancel', cleanup)
    }
    handle.addEventListener('pointermove', onMove)
    handle.addEventListener('pointerup', cleanup)
    handle.addEventListener('pointercancel', cleanup)
  }, [])

  return { open, full, widthPx, setOpen, toggleFull, onHandlePointerDown }
}
